import { useId, useMemo, useState } from "react";
import { hasMilestoneComponent } from "@common/schemas/domains/accounting/scheduleMilestone.schema";
import { useFormikContext } from "formik";
import { SCHEDULE_POLICY_KIND_PERIOD_COPY } from "@common/helpers/domains/accounting/schedulePolicyKind";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import classNames from "classnames";
import { getApiErrorMessage } from "@api/apiError";
import { usePreviewScheduleRecognition } from "@api/scheduleTemplate.api";
import { useDebouncedEffect } from "@hooks/useDebouncedEffect";
import { NumberFormat } from "@design-system/NumberFormat/NumberFormat";
import {
    SchedulePreviewLadderResult,
    SchedulePreviewPaneFrame,
} from "./SchedulePreviewLadderTable";
import { ScheduleMilestonePreviewDates } from "./ScheduleMilestonePreviewDates";
import {
    resolvePreviewAnchorUsage,
    buildPreviewLadder,
    toAnchorsByComponentOrder,
} from "./schedulePreviewHelpers";
import { buildPreviewRequest, PreviewRequest } from "./schedulePreviewRequest";
import { ScheduleTemplateFormValues } from "./scheduleFormHelpers";
import { SOURCE_DATE_LABEL } from "./scheduleRecognitionUi";
import { useAnsweredPreviewBody } from "./useAnsweredPreviewBody";
import styles from "./ScheduleDrawer.module.scss";
import { ScheduleNumericInput } from "./ScheduleNumericInput";

const PREVIEW_DEBOUNCE_MS = 400;

const fields = createFormikFields<ScheduleTemplateFormValues>();

interface SchedulePreviewPaneProps {
    values: ScheduleTemplateFormValues;
}

export const SchedulePreviewPane: React.FC<SchedulePreviewPaneProps> = ({
    values,
}) => {
    const sourceDateLabelId = useId();
    const specificDateLabelId = useId();
    const { setFieldValue } = useFormikContext<ScheduleTemplateFormValues>();
    const isMilestone = hasMilestoneComponent(values.components);
    const preview = useMemo(() => buildPreviewRequest(values), [values]);
    const previewRequest = preview.request;
    const [debouncedRequest, setDebouncedRequest] =
        useState<PreviewRequest | null>(previewRequest);

    useDebouncedEffect(
        () => {
            setDebouncedRequest(previewRequest);
        },
        [previewRequest],
        PREVIEW_DEBOUNCE_MS,
    );

    const previewQuery = usePreviewScheduleRecognition(
        debouncedRequest?.body ?? null,
    );
    const answeredRequest = useAnsweredPreviewBody({
        requestedBody: debouncedRequest,
        query: previewQuery,
    });
    const anchorUsage = resolvePreviewAnchorUsage(values.components);
    const showsSourceDate = !isMilestone && anchorUsage.usesSourceDate;
    const showsSpecificDate = !isMilestone && anchorUsage.usesSpecificDate;

    const ladder = useMemo(() => {
        if (!previewQuery.data || !answeredRequest) {
            return [];
        }
        return buildPreviewLadder({
            rows: previewQuery.data.rows,
            totalAmount: previewQuery.data.totalAmount,
            milestoneNames: answeredRequest.body.milestones,
            periodLabelContext: {
                termUnit: answeredRequest.body.termUnit,
                sourceDate: answeredRequest.labelDates.sourceDate,
                specificDate: answeredRequest.labelDates.specificDate,
                anchorsByComponentOrder: toAnchorsByComponentOrder(
                    answeredRequest.body.components,
                ),
            },
        });
    }, [previewQuery.data, answeredRequest]);

    const isAwaitingRequest =
        debouncedRequest == null && previewRequest != null;
    const unavailableMessage =
        debouncedRequest != null || isAwaitingRequest
            ? null
            : preview.unavailableMessage;

    return (
        <SchedulePreviewPaneFrame>
            <div className={styles.exampleAmountRow}>
                {showsSourceDate && (
                    <>
                        <span
                            id={sourceDateLabelId}
                            className={styles.inlineLabel}
                        >
                            {SOURCE_DATE_LABEL}
                        </span>
                        <fields.Date
                            name="exampleSourceDate"
                            aria-labelledby={sourceDateLabelId}
                            className={styles.inlineDateField}
                            clearable
                            emptyValue={undefined}
                            placeholder="Select"
                            buttonProps={{
                                variant: "primary",
                                size: "md",
                            }}
                        />
                    </>
                )}
                {showsSpecificDate && (
                    <>
                        <span
                            id={specificDateLabelId}
                            className={styles.inlineLabel}
                        >
                            Specific date
                        </span>
                        <fields.Date
                            name="exampleSpecificDate"
                            aria-labelledby={specificDateLabelId}
                            className={styles.inlineDateField}
                            clearable
                            emptyValue={undefined}
                            placeholder="Select"
                            buttonProps={{
                                variant: "primary",
                                size: "md",
                            }}
                        />
                    </>
                )}
                <span className={styles.inlineLabel}>Example amount</span>
                <NumberFormat
                    value={values.exampleAmount}
                    onValueChange={(numberValues) =>
                        setFieldValue(
                            "exampleAmount",
                            numberValues.floatValue ?? 0,
                        )
                    }
                    prefix="$ "
                    decimalScale={2}
                    thousandSeparator
                    allowNegative={false}
                    customInput={ScheduleNumericInput}
                    className={classNames(
                        styles.drawerInput,
                        styles.numericInput,
                        styles.exampleAmountInput,
                    )}
                />
            </div>
            {isMilestone && (
                <ScheduleMilestonePreviewDates
                    milestones={values.milestones}
                    sourceDate={values.milestoneExampleSourceDate}
                    exampleDates={values.milestoneExampleDates}
                    onExampleDatesChange={(exampleDates) =>
                        setFieldValue("milestoneExampleDates", exampleDates)
                    }
                />
            )}
            <SchedulePreviewLadderResult
                unavailableMessage={unavailableMessage}
                errorMessage={
                    previewQuery.isError
                        ? getApiErrorMessage(
                              previewQuery.error,
                              "Couldn't load preview.",
                          )
                        : null
                }
                isLoading={
                    isAwaitingRequest ||
                    (previewQuery.data == null && previewQuery.isFetching)
                }
                ladder={ladder}
                periodCopy={SCHEDULE_POLICY_KIND_PERIOD_COPY[values.kind]}
            />
        </SchedulePreviewPaneFrame>
    );
};
